import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAssessmentStore } from '../store/assessment.store';
import { PremiumJourney } from '../components/premium/PremiumJourney';
import { Card } from '../components/ui/Card';
import { PremiumLayout } from '../components/premium/PremiumLayout';
import { apiService } from '../services/apiService';
import { FeedbackForm } from '../components/feedback/FeedbackForm';
import { EmojiRating } from '../components/feedback/EmojiRating';
import { MessageSquare } from 'lucide-react';
import type { DetailedAssessmentResult } from '../types';

export const ResultsPage: FC = () => {
    const { assessmentId } = useParams<{ assessmentId: string }>();
    const { result } = useAssessmentStore();
    const navigate = useNavigate();
    
    const [detailedResult, setDetailedResult] = useState<DetailedAssessmentResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [feedbackGiven, setFeedbackGiven] = useState(false);

    useEffect(() => {
        // Evita requisições duplicadas se já tem dados
        if (assessmentId && !detailedResult) {
            let isCancelled = false; // Cleanup flag

            const fetchResult = async () => {
                try {
                    setIsLoading(true);
                    setError(null);
                    const data = await apiService.getAssessmentResult(assessmentId);
                    
                    // Só atualiza state se componente ainda está montado
                    if (!isCancelled) {
                        setDetailedResult(data);
                    }
                } catch (err) {
                    if (!isCancelled) {
                        setError(err instanceof Error ? err.message : 'Erro ao carregar resultado');
                    }
                } finally {
                    if (!isCancelled) {
                        setIsLoading(false);
                    }
                }
            };

            fetchResult();

            // Cleanup function
            return () => {
                isCancelled = true;
            };
        }
    }, [assessmentId]);

    // Loading state
    if (isLoading) {
        return (
            <div className="max-w-2xl mx-auto my-12">
                <Card variant="elegant" padding="xl">
                    <div className="text-center py-12 space-y-4">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="text-neutral-600">Carregando resultado...</p>
                    </div>
                </Card>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="max-w-2xl mx-auto my-12">
                <Card variant="elegant" padding="xl">
                    <div className="text-center py-12 space-y-4">
                        <p className="text-red-600">{error}</p>
                        <button
                            onClick={() => navigate('/results')}
                            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg"
                        >
                            Voltar aos Resultados
                        </button>
                    </div>
                </Card>
            </div>
        );
    }

    // Render function for results with feedback
    const renderResultsWithFeedback = (resultData: DetailedAssessmentResult) => (
        <PremiumLayout>
            <PremiumJourney result={resultData} />
            
            {/* Feedback Section */}
            <div className="max-w-4xl mx-auto mt-12 mb-8">
                <Card variant="elegant" padding="xl">
                    <div className="space-y-8">
                        {/* Header */}
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                Como foi sua experiência?
                            </h3>
                            <p className="text-gray-600">
                                Seu feedback nos ajuda a aprimorar nossos serviços
                            </p>
                        </div>

                        {/* Quick emoji rating */}
                        <div className="w-full px-4 md:px-8 flex justify-center mt-4">
                            <div className="w-full max-w-2xl relative z-10">
                                <EmojiRating 
                                    label="Qual sua avaliação geral?"
                                    context={{
                                        component: 'ResultsPage',
                                        feature: 'QuickRating',
                                        assessmentId: assessmentId || '',
                                        assessmentType: 'communication-profile-v2'
                                    }}
                                    onRating={(rating, comment, context) => {
                                        console.log('Rating:', rating, 'Comment:', comment, 'Context:', context);
                                        // TODO: Send to feedback service with context
                                        setFeedbackGiven(true);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Detailed feedback button */}
                        {!feedbackGiven && (
                            <div className="text-center">
                                <button
                                    onClick={() => setShowFeedbackForm(true)}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                                >
                                    <MessageSquare size={20} />
                                    Dar Feedback Detalhado
                                </button>
                                <p className="text-sm text-gray-500 mt-2">
                                    Nos ajude a melhorar com seu feedback específico
                                </p>
                            </div>
                        )}

                        {feedbackGiven && (
                            <div className="text-center py-4">
                                <div className="inline-flex items-center gap-2 text-green-600 font-medium">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    Obrigado pelo seu feedback!
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* Feedback Form Modal */}
            {showFeedbackForm && assessmentId && (
                <FeedbackForm
                    assessmentId={assessmentId}
                    assessmentType="communication-profile-v2"
                    onClose={() => setShowFeedbackForm(false)}
                    onSubmitted={() => {
                        setShowFeedbackForm(false);
                        setFeedbackGiven(true);
                    }}
                />
            )}
        </PremiumLayout>
    );

    // If we have a detailed result from API, use it
    if (detailedResult) {
        return renderResultsWithFeedback(detailedResult);
    }

    // If we have a result from the store (current assessment), use it
    if (result) {
        return renderResultsWithFeedback(result);
    }

    // No result available
    return (
        <div className="max-w-2xl mx-auto my-12">
            <Card variant="elegant" padding="xl">
                <div className="text-center py-12 space-y-4">
                    <p className="text-neutral-600">Nenhum resultado para exibir.</p>
                    <button
                        onClick={() => navigate('/assessment')}
                        className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg"
                    >
                        Iniciar Novo Questionário
                    </button>
                </div>
            </Card>
        </div>
    );
};